import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Invoices.css'; // reuse same styling
import { FaCalendarCheck, FaSearch, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { showToast, hideToast, updateToast } from '../utils/toast';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const SORT_KEY = 'monthlyInvoiceSort';

// simple local storage helpers
function safeLocalGet(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function safeLocalSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
function setUserPref(key, val) {
  safeLocalSet(key, val);
}
function getUserPref(key) {
  return safeLocalGet(key);
}
function getUserFullName() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    const nm = [u.firstname, u.lastname].filter(Boolean).join(' ');
    return nm || u.name || u.username || '';
  } catch { return ''; }
}

export default function MonthlyInvoicing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ offset: 0, limit: DEFAULT_LIMIT, total: 0 });

  // filters (similar to Invoices)
  const [q, setQ] = useState('');

  // modal + warehouses
  const [miOpen, setMiOpen] = useState(false);
  const [miClient, setMiClient] = useState(null);
  const [miSelectedIds, setMiSelectedIds] = useState([]);
  const [lastResults, setLastResults] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [miSubmitting, setMiSubmitting] = useState(false);
  const [miForm, setMiForm] = useState({
    warehouseId: 'all',            // 'all' or a specific id
    perWarehouse: false,           // separate invoice per warehouse
    month: '',                     // YYYY-MM
    invoiceDate: new Date().toISOString().slice(0,10), // YYYY-MM-DD
    includeAwaitingPickup: false,
    allowMinimumWhenNoReceipts: false,
  });

  // persisted sort
  const initialSort = useMemo(() => {
    try {
      const saved = safeLocalGet(SORT_KEY) || getUserPref(SORT_KEY);
      return saved || { field: 'name', dir: 'ASC' };
    } catch { return { field: 'name', dir: 'ASC' }; }
  }, []);
  const [sort, setSort] = useState(initialSort);
  const [selected, setSelected] = useState(new Set());
  const selAllRef = useRef(null);
  useEffect(() => {
    if (selAllRef.current) {
      selAllRef.current.indeterminate = selected.size > 0 && selected.size < rows.length;
    }
  }, [selected, rows.length]);

  useEffect(() => {
    safeLocalSet(SORT_KEY, sort);
    setUserPref(SORT_KEY, sort);
  }, [sort]);

  // load warehouses once for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/warehouses`);
        const j = await res.json();
        setWarehouses(Array.isArray(j) ? j : []);
      } catch (e) {
        console.error('warehouses load failed', e);
        setWarehouses([]);
      }
    })();
  }, []);

  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Monthly Invoicing ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  useEffect(() => { fetchClients(0, meta.limit); }, [q, sort]);

  async function fetchClients(offset = 0, limit = DEFAULT_LIMIT) {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('offset', String(offset));
      qs.set('limit', String(limit));
      qs.set('activeOnly', '1');
      if (q) qs.set('q', q);
      // include sorting per clients.js API
      const sf = (sort && sort.field) ? sort.field : 'name';
      const so = (sort && sort.dir) ? sort.dir : 'ASC';
      qs.set('sortField', sf);
      qs.set('sortOrder', so);

      const res = await fetch(`${API}/api/clients?${qs.toString()}`);
      const j = await res.json();

      const list = Array.isArray(j?.data) ? j.data
        : (Array.isArray(j?.rows) ? j.rows
        : (Array.isArray(j) ? j : []));
      const total = (j?.meta && typeof j.meta.total === 'number') ? j.meta.total
        : (typeof j?.total === 'number' ? j.total : list.length);

      setRows(list);
      setSelected(new Set());
      setMeta({ offset, limit, total });
    } catch (e) {
      console.error('fetch clients failed', e);
      setRows([]);
      setSelected(new Set());
      setMeta({ offset, limit, total: 0 });
    } finally { setLoading(false); }
  }

  function handleRun() { fetchClients(0, meta.limit); }
  function handleClear() { setQ(''); fetchClients(0, meta.limit); }

  function toggleSort(field) {
    setSort((prev) => {
      const dir = prev.field === field ? (prev.dir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
      return { field, dir };
    });
  }
  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (checked) {
      setSelected(new Set(rows.map(r => r.id)));
    } else {
      setSelected(new Set());
    }
  };

  const sorted = useMemo(() => {
    const data = [...rows];
    const { field, dir } = sort || { field: 'name', dir: 'ASC' };
    data.sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      const isNum = ['id','paymentterms','status'].includes(field);
      if (isNum) {
        const an = Number(aVal ?? 0);
        const bn = Number(bVal ?? 0);
        return dir === 'ASC' ? an - bn : bn - an;
      } else {
        const av = String(aVal ?? '').toLowerCase();
        const bv = String(bVal ?? '').toLowerCase();
        if (av < bv) return dir === 'ASC' ? -1 : 1;
        if (av > bv) return dir === 'ASC' ? 1 : -1;
        return 0;
      }
    });
    return data;
  }, [rows, sort]);

  function iconFor(field) {
    if (!sort || sort.field !== field) return <FaSort />;
    return sort.dir === 'ASC' ? <FaSortUp /> : <FaSortDown />;
  }

  function createMonthlyInvoice(client) {
    setMiClient(client);
    setMiSelectedIds(client?.id ? [client.id] : []);
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    setMiForm(f => ({ ...f, month: `${y}-${m}`, warehouseId: 'all', perWarehouse: false, includeAwaitingPickup: false }));
    setMiOpen(true);
  }

  function createMonthlyForSelected(){
    const ids = Array.from(selected || []);
    if (!ids.length) { alert('Select at least one client.'); return; }
    // If exactly one, try to prefill client name by finding in rows
    const first = ids.length === 1 ? rows.find(r => r.id === ids[0]) : null;
    setMiClient(first || null);
    setMiSelectedIds(ids);
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    setMiForm(f => ({ ...f, month: `${y}-${m}`, warehouseId: 'all', perWarehouse: false, includeAwaitingPickup: false }));
    setMiOpen(true);
  }

  async function submitMonthlyInvoice(e){
    e?.preventDefault?.();
    if (!miForm.month) { alert('Please select which month this invoice is for.'); return; }
    const ids = miSelectedIds.length ? miSelectedIds : (miClient?.id ? [miClient.id] : []);
    if (!ids.length) { alert('No client selected'); return; }

    try {
      setMiSubmitting(true);
      showToast({ id: 'monthly.create', message: 'Creating monthly invoices…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
      const payload = {
        clientIds: ids,
        warehouseId: miForm.warehouseId === 'all' ? 'all' : Number(miForm.warehouseId),
        useOrderPallets: miForm.includeAwaitingPickup || false,
        invoiceDate: miForm.invoiceDate,
        whichMonth: miForm.month,
        createdBy: { name: getUserFullName() || 'system' },
        allowMinimumWhenNoReceipts: !!miForm.allowMinimumWhenNoReceipts,
      };
      const res = await fetch(`${API}/api/invoices/monthly/storage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      const ok = Array.isArray(j?.results) ? j.results.filter(r=>r.ok) : [];
      const fail = Array.isArray(j?.results) ? j.results.filter(r=>!r.ok) : [];
      setLastResults({ ok, fail });
      updateToast({ id: 'monthly.create', message: `Monthly invoices created: ${ok.length}/${ids.length}${fail.length ? ` (failed ${fail.length})` : ''}` });
      setTimeout(() => hideToast('monthly.create'), 2500);
      // Optionally keep a dialog alert too
      // alert(`Monthly invoices: Success ${ok.length}/${ids.length}${fail.length ? `, Failed ${fail.length}` : ''}`);
      try { window.dispatchEvent(new CustomEvent('invoices:refresh')); } catch {}
      setMiOpen(false);
    } catch (err) {
      console.error('monthly create failed', err);
      updateToast({ id: 'monthly.create', message: err.message || 'Failed to create monthly invoices' });
      setTimeout(() => hideToast('monthly.create'), 3000);
    }
    finally { setMiSubmitting(false); }
  }

  return (
    <div className="invoices-wrapper">
      <div className="invoice-filters">
        <div className="preset-controls">
          <label>Search</label>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
            <FaSearch />
            <input
              type="text"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search clients…"
            />
          </div>
        </div>
        <button className="btn" onClick={handleRun} title="Run">Run</button>
        <button className="btn" onClick={handleClear} title="Reset">Reset</button>
        <button
          className="btn primary"
          disabled={selected.size === 0}
          onClick={createMonthlyForSelected}
          title={selected.size ? `Create for ${selected.size} selected client(s)` : 'Select clients first'}
        >
          Create for Selected ({selected.size || 0})
        </button>
      </div>

      <div className="invoice-table-wrapper">
        <table className="invoice-table">
          <thead>
            <tr>
              <th style={{ width: 34, textAlign:'center' }}>
                <input
                  type="checkbox"
                  ref={selAllRef}
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(e)=>toggleAll(e.target.checked)}
                  style={{ width:16, height:16 }}
                />
              </th>
              <th className="actions-cell">Actions</th>
              <th onClick={()=>toggleSort('id')} className={sort.field==='id' ? 'active-sort' : ''}>
                ID {iconFor('id')}
              </th>
              <th onClick={()=>toggleSort('name')} className={sort.field==='name' ? 'active-sort' : ''}>
                Name {iconFor('name')}
              </th>
              <th onClick={()=>toggleSort('contact')} className={sort.field==='contact' ? 'active-sort' : ''}>
                Contact {iconFor('contact')}
              </th>
              <th onClick={()=>toggleSort('email')} className={sort.field==='email' ? 'active-sort' : ''}>
                Email {iconFor('email')}
              </th>
              <th onClick={()=>toggleSort('paymentterms')} className={sort.field==='paymentterms' ? 'active-sort' : ''}>
                Terms {iconFor('paymentterms')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id}>
                <td style={{ textAlign:'center' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={()=>toggleRow(c.id)}
                    style={{ width:16, height:16 }}
                  />
                </td>
                <td className="actions-cell">
                  <button className="mini-btn" title="Create monthly invoice" onClick={()=>createMonthlyInvoice(c)}>
                    <FaCalendarCheck size={16} />
                  </button>
                </td>
                <td className="nowrap">{c.id}</td>
                <td className="nowrap">{c.name}</td>
                <td className="nowrap">{c.contact}</td>
                <td className="nowrap">{c.email}</td>
                <td className="nowrap">{c.paymentterms}</td>
              </tr>
            ))}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} style={{ opacity:.7, textAlign:'center', padding:'1rem' }}>No clients found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {lastResults && (lastResults.ok?.length > 0 || lastResults.fail?.length > 0) && (
        <div className="toast" style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid #444', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Monthly invoice results</div>
          {lastResults.ok?.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              Created:&nbsp;
              {lastResults.ok.map(r => r.invoiceId).slice(0,10).map((id) => (
                <a key={id} href={`#/invoice/${id}`} style={{ marginRight: 8, textDecoration: 'underline' }}>#{id}</a>
              ))}
              {lastResults.ok.length > 10 ? `… (+${lastResults.ok.length - 10} more)` : ''}
            </div>
          )}
          {lastResults.fail?.length > 0 && (
            <div style={{ opacity: .8 }}>
              Failed client(s): {lastResults.fail.map(r => r.clientId).join(', ')}
            </div>
          )}
        </div>
      )}

      {miOpen && (
        <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) setMiOpen(false); }}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="miTitle"
            style={{ width: '50vw', maxWidth: '50vw' }}
          >
            <h2 id="miTitle" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FaCalendarCheck /> Create Monthly Invoice{miSelectedIds?.length > 1 ? ` — ${miSelectedIds.length} clients` : (miClient?.name ? ` — ${miClient.name}` : '')}
            </h2>
            <form onSubmit={submitMonthlyInvoice} className="payment-form">
              <div className="form-grid" style={{ gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap: '0.6rem 0.8rem' }}>
                <label>Warehouse(s) selection
                  <select
                    value={miForm.warehouseId}
                    onChange={(e)=>setMiForm(f=>({ ...f, warehouseId: e.target.value }))}
                  >
                    <option value="all">All</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={String(w.id)}>{w.name || `Warehouse #${w.id}`}</option>
                    ))}
                  </select>
                </label>

                <label>Which month is this invoice for?
                  {(() => {
                    const now = new Date();
                    const curYear = now.getFullYear();
                    const ym = String(miForm.month || '').split('-');
                    const selYear = ym[0] && ym[0].length === 4 ? ym[0] : String(curYear);
                    const selMonth = ym[1] ? ym[1].padStart(2, '0') : String(now.getMonth() + 1).padStart(2, '0');
                    const MONTHS = [
                      ['01','January'], ['02','February'], ['03','March'], ['04','April'],
                      ['05','May'], ['06','June'], ['07','July'], ['08','August'],
                      ['09','September'], ['10','October'], ['11','November'], ['12','December'],
                    ];
                    const YEARS = Array.from({ length: 7 }, (_, i) => String(curYear - 3 + i));
                    const setMonth = (mm) => setMiForm(f => ({ ...f, month: `${selYear}-${mm}` }));
                    const setYear  = (yy) => setMiForm(f => ({ ...f, month: `${yy}-${selMonth}` }));
                    return (
                      <div style={{ display:'flex', gap: 8 }}>
                        <select value={selMonth} onChange={(e) => setMonth(e.target.value)}>
                          {MONTHS.map(([val, name]) => (
                            <option key={val} value={val}>{name}</option>
                          ))}
                        </select>
                        <select value={selYear} onChange={(e) => setYear(e.target.value)}>
                          {YEARS.map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </label>

                <div style={{ display:'flex', gap:'2rem', marginTop:'0.5rem', gridColumn:'1 / -1' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px' }}
                      checked={miForm.perWarehouse}
                      onChange={(e)=>setMiForm(f=>({ ...f, perWarehouse: e.target.checked }))}
                    />
                    <span>Separate Invoice For Each Warehouse Location</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px' }}
                      checked={miForm.includeAwaitingPickup}
                      onChange={(e)=>setMiForm(f=>({ ...f, includeAwaitingPickup: e.target.checked }))}
                    />
                    <span>Include pallets from orders awaiting pickup?</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px' }}
                      checked={miForm.allowMinimumWhenNoReceipts || false}
                      onChange={(e)=>setMiForm(f=>({ ...f, allowMinimumWhenNoReceipts: e.target.checked }))}
                    />
                    <span>Allow minimum-only if no receipts</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions" style={{ justifyContent:'flex-end', marginTop: 12 }}>
                <button type="button" className="btn" onClick={()=>setMiOpen(false)} disabled={miSubmitting}>Cancel</button>
                <button type="submit" className="btn primary" disabled={miSubmitting}>{miSubmitting ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
